import { useCallback, useEffect, useState } from "react";
import {
  ArrowRightCircle,
  Check,
  ChevronDown,
  History,
  Hourglass,
  Info,
  Lock,
  Mail,
  MailOpen,
  MessageSquareText,
  Search,
  Target,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ApiError, api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useAdminUnlock } from "../components/GlobalViewAs";
import { useTabParam } from "../hooks/useTabParam";
import BottomNav from "../components/BottomNav";
import ColleagueFeedbackMatrix from "../components/ColleagueFeedbackMatrix";
import EmployeeSearch from "../components/EmployeeSearch";
import ReviewStagePips from "../components/ReviewStagePips";
import { reviewStatusLabel, reviewStatusTone } from "../utils/reviewStatus";
import AppraisalJourney from "../components/appraisal/AppraisalJourney";
import MeetingPanel, { MyMeetingCard, isMeetingStage, type MeetingInfo } from "../components/appraisal/MeetingPanel";
import MyObjectivesCard from "../components/appraisal/Objectives";
import {
  appraisalNow,
  daysUntil,
  dueText,
  formatDate,
  appraisalName,
  isYourMove,
  ratingScale,
  ratingText,
  reflectionScale,
  selfRatingText,
} from "../utils/appraisal";

interface Review {
  id: number;
  type?: string;
  cycle_name?: string;
  employee_id?: number;
  employee_name?: string;
  manager_id?: number;
  status: string;
  review_period_start: string | null;
  review_period_end: string | null;
  overall_rating: number | null;
  self_rating?: number | null;
  overall_self_rating?: number | null;
  manager_rating?: number | null;
  overall_manager_rating?: number | null;
  overall_agreed_rating?: number | null;
  completed_date?: string | null;
  self_assessment_deadline?: string | null;
  colleague_feedback_deadline?: string | null;
  manager_review_deadline?: string | null;
  recommendation?: string | null;
  is_historical?: boolean;
  historical_summary?: string | null;
  review_date?: string | null;
  colleague_count?: number;
  manager_name?: string | null;
  opens_on?: string | null;
  triggered?: boolean;
  appraisal_year?: number | null;
  rating_words?: boolean;
  /** The booked review meeting, once there is one. */
  meeting?: MeetingInfo | null;
  meeting_date?: string | null;
  employee_active?: boolean;
  feedback_released?: boolean;
  has_response?: boolean;
  objectives_total?: number;
  objectives_done?: number;
  check_in_plan?: { cadence_label: string; stopped: boolean } | null;
}

interface FeedbackResponse {
  id: number;
  question: string;
  answer: string | null;
  rating: number | null;
  sort_order: number;
}

interface ColleagueFeedbackItem {
  id: number;
  colleague_id: number;
  colleague_name: string;
  focus_area: string;
  status: string;
  deadline?: string | null;
  submitted_at?: string | null;
  invited_at?: string | null;
  responses?: FeedbackResponse[];
}

interface ActionTaskRow {
  id: number;
  title: string;
  description: string | null;
  task_type: string;
  source_type: string | null;
  employee_id: number;
  employee_name: string | null;
  assigned_to_id: number;
  due_date: string | null;
  status: string;
  priority: string;
  action_url: string | null;
  completed_at: string | null;
  completion_notes: string | null;
}

// "goals" was retired on 21 Oct 2026: the objectives agreed in the appraisal
// are the goals, shown at the top of My appraisal. Old ?tab=goals links fall
// back to My appraisal.
const PERF_TABS = ["my-reviews", "team-reviews", "tasks"] as const;
type PerfTab = typeof PERF_TABS[number];

const DATE_FMT = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });

const reviewStatusBadge = reviewStatusTone;

export default function PerformancePage() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const showTeamTab = hasPermission("can_view_team") || hasPermission("can_approve_leave");
  const [tab, setTab] = useTabParam<PerfTab>(PERF_TABS, "my-reviews");
  // My tasks only appears once someone actually has a task (they come out of
  // appraisals), so the page isn't cluttered for everyone else.
  const [hasTasks, setHasTasks] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [open, done] = await Promise.all([
          api.get<ActionTaskRow[]>("/api/tasks/my"),
          api.get<ActionTaskRow[]>("/api/tasks/my?status=completed"),
        ]);
        setHasTasks(open.length > 0 || done.length > 0);
      } catch { /* ignore */ }
    })();
  }, []);

  const showTasks = hasTasks || tab === "tasks";

  return (
    <div className="flex min-h-dvh flex-col bg-pme-paper safe-x">
      <header className="safe-top px-5 pt-4 pb-3">
        <h1 className="text-2xl font-semibold text-gray-900">{t("nav.performance")}</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          {t("appraisal.pageIntro", "Your appraisals, and feedback colleagues have asked you for.")}
        </p>
      </header>
      <nav className="flex overflow-x-auto border-b border-gray-200 bg-white px-2 text-sm">
        <TabBtn active={tab === "my-reviews"} onClick={() => setTab("my-reviews")}>{t("appraisal.tabMine", "My appraisal")}</TabBtn>
        {showTeamTab && (
          <TabBtn active={tab === "team-reviews"} onClick={() => setTab("team-reviews")}>{t("appraisal.tabTeam", "My team's appraisals")}</TabBtn>
        )}
        {showTasks && (
          <TabBtn active={tab === "tasks"} onClick={() => setTab("tasks")}>{t("tasks.myTasks")}</TabBtn>
        )}
      </nav>
      <main className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto px-5 py-4 pb-24 space-y-2">
        {tab === "my-reviews" && <MyReviewsTab />}
        {tab === "team-reviews" && showTeamTab && <TeamReviewsTab />}
        {tab === "tasks" && <TasksTab />}
      </main>
      <BottomNav />
    </div>
  );
}

/* ── My appraisals tab ───────────────────────────────────────────────────── */

interface FeedbackRequest {
  id: number;
  review_id: number;
  employee_name: string;
  focus_area: string;
  status: string;
  deadline: string | null;
  submitted_at?: string | null;
  available?: boolean;
  appraisal_year?: number | null;
}

function MyReviewsTab() {
  const { t } = useTranslation();
  const { employee } = useAuth();
  const { adminUnlocked } = useAdminUnlock();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [requests, setRequests] = useState<FeedbackRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [respondingId, setRespondingId] = useState<number | null>(null);
  const [responseText, setResponseText] = useState("");
  const [responseSaving, setResponseSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const loadReviews = useCallback(async () => {
    const url = adminUnlocked ? "/api/performance/my-reviews?admin_view=true" : "/api/performance/my-reviews";
    const [mine, asked] = await Promise.allSettled([
      api.get<Review[]>(url),
      api.get<FeedbackRequest[]>("/api/reviews/my-colleague-feedbacks"),
    ]);
    if (mine.status === "fulfilled") setReviews(mine.value);
    if (asked.status === "fulfilled") setRequests(asked.value);
    setLoading(false);
  }, [adminUnlocked]);

  useEffect(() => { void loadReviews(); }, [loadReviews]);

  const handleSubmitResponse = async (reviewId: number) => {
    setResponseSaving(true);
    try {
      await api.put(`/api/reviews/${reviewId}/employee-response`, { employee_response: responseText });
      setRespondingId(null);
      setResponseText("");
      setToast(t("appraisal.responseSaved", "Response saved"));
      setTimeout(() => setToast(null), 3000);
      void loadReviews();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setResponseSaving(false);
  };

  if (loading) return <p className="py-8 text-center text-sm text-gray-400">{t("common.loading")}</p>;

  const live = reviews.filter(
    (r) => r.type === "360" && !r.is_historical && r.status !== "completed" && r.status !== "cancelled",
  );
  const current = live[0] ?? null;
  const past = reviews.filter((r) => r !== current && r.status !== "cancelled" && !live.includes(r));
  const openRequests = requests.filter((q) => q.status !== "submitted" && q.available !== false);
  const givenCount = requests.filter((q) => q.status === "submitted").length;

  return (
    <div className="space-y-5">
      {current && <CurrentAppraisal review={current} />}
      {/* The objectives agreed in the last appraisal: the goals for the year. */}
      <MyObjectivesCard />
      {current ? null : (
        <section className="card p-5 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-pme-blue/10 text-pme-blue">
            <MailOpen size={22} strokeWidth={1.75} aria-hidden />
          </div>
          <h2 className="mt-3 text-base font-semibold text-pme-navy">
            {t("appraisal.emptyTitle", "No appraisal open right now")}
          </h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-600">
            {t(
              "appraisal.emptyBody",
              "When your appraisal starts you'll get an email invitation with a link straight to it. You'll begin with your self-assessment.",
            )}
          </p>
        </section>
      )}

      {openRequests.length > 0 && (
        <section>
          <h2 className="eyebrow mb-2">{t("appraisal.askedTitle", "Feedback you've been asked to give")}</h2>
          <div className="space-y-2">
            {openRequests.map((q) => {
              const days = daysUntil(q.deadline);
              return (
                <Link
                  key={q.id}
                  to={`/review?feedback=${q.id}`}
                  className="card flex items-center gap-3 p-4 transition hover:shadow-md"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-700">
                    <MessageSquareText size={18} strokeWidth={1.75} aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">
                      {q.employee_name}
                      <span className="ml-1.5 text-xs font-normal text-gray-500">· {appraisalName(q.appraisal_year)}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      {t("appraisal.askedLine", "About 10 minutes. They won't see your name.")}
                    </p>
                    {q.deadline && (
                      <p className={`text-xs ${days != null && days < 0 ? "font-semibold text-red-600" : "text-gray-500"}`}>
                        {t("appraisal.byDate", "By {{date}}", { date: formatDate(q.deadline) })} · {dueText(q.deadline)}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 rounded-lg bg-purple-600 px-3 py-2 text-xs font-semibold text-white">
                    {q.status === "in_progress" ? t("appraisal.continueFeedback", "Continue") : t("appraisal.giveFeedback", "Give feedback")}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}
      {givenCount > 0 && openRequests.length === 0 && (
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <Check size={14} strokeWidth={2.5} aria-hidden className="text-green-600" />
          {t("appraisal.givenCount", "You've given feedback on {{count}} colleagues. Thank you.", { count: givenCount })}
        </p>
      )}

      <HowAppraisalsWork defaultOpen={!current} />

      {past.length > 0 && (
        <section>
          <h2 className="eyebrow mb-2">{t("appraisal.pastTitle", "Past appraisals")}</h2>
          <div className="space-y-2">
            {past.map((r) => {
              const is360 = r.type === "360";
              const agreed = r.overall_agreed_rating ?? r.overall_manager_rating ?? r.manager_rating;
              const selfRating = r.overall_self_rating ?? r.self_rating ?? r.overall_rating;
              const isOwnReview = employee && r.employee_id === employee.employee_id;
              const title = is360
                ? appraisalName(r.appraisal_year)
                : `${formatDate(r.review_period_start)} – ${formatDate(r.review_period_end)}`;
              return (
                <div key={`${r.type ?? "perf"}-${r.id}`} className="card p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {title}
                        {r.is_historical && (
                          <span className="ml-2 inline-block rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">
                            {t("appraisal.historical", "Recorded")}
                          </span>
                        )}
                      </p>
                      {is360 && r.review_period_start && (
                        <p className="text-xs text-gray-500">
                          {formatDate(r.review_period_start)} – {formatDate(r.review_period_end)}
                        </p>
                      )}
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${reviewStatusBadge(r.status)}`}>
                      {reviewStatusLabel(r.status)}
                    </span>
                  </div>
                  <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    {selfRating != null && (
                      <div>
                        <dt className="text-gray-400">{r.rating_words ? t("appraisal.yourView", "Your view") : t("appraisal.yourRating", "Your rating")}</dt>
                        <dd className="font-medium text-gray-700">{is360 ? selfRatingText(selfRating, r.rating_words) : ratingText(selfRating)}</dd>
                      </div>
                    )}
                    {agreed != null && (
                      <div>
                        <dt className="text-gray-400">{t("appraisal.agreedRating", "Agreed rating")}</dt>
                        <dd className="font-medium text-gray-700">{ratingText(agreed)}</dd>
                      </div>
                    )}
                  </dl>
                  <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
                    {is360 && (
                      <Link to={`/review?review=${r.id}`} className="text-xs font-semibold text-pme-blue hover:underline">
                        {t("appraisal.viewOutcome", "Read the outcome")}
                      </Link>
                    )}
                    {r.status === "completed" && isOwnReview && respondingId !== r.id && (
                      <button type="button" onClick={() => setRespondingId(r.id)} className="text-xs font-semibold text-pme-blue hover:underline">
                        {t("appraisal.addResponse", "Add your response")}
                      </button>
                    )}
                  </div>
                  {respondingId === r.id && (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={responseText}
                        onChange={(e) => setResponseText(e.target.value)}
                        rows={3}
                        placeholder={t("appraisal.responsePlaceholder", "Your thoughts on the outcome of this appraisal…")}
                        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-pme-blue focus:outline-none"
                      />
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => { setRespondingId(null); setResponseText(""); }} className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100">
                          {t("common.cancel")}
                        </button>
                        <button type="button" onClick={() => void handleSubmitResponse(r.id)} disabled={responseSaving || !responseText.trim()} className="rounded bg-pme-blue px-3 py-1 text-xs font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50">
                          {responseSaving ? t("common.saving", "Saving…") : t("appraisal.saveResponse", "Save response")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

/* The appraisal that is running now: what's happening, the one button, the
   journey with dates. */
function CurrentAppraisal({ review }: { review: Review }) {
  const { t } = useTranslation();
  const manager = review.manager_name ?? null;
  const notOpen = review.triggered === false;
  const now = appraisalNow(review.status, manager);
  const yourMove = !notOpen && isYourMove(review.status);
  const selfDue = review.self_assessment_deadline;
  const days = daysUntil(selfDue);
  const selfStage = review.status === "not_started" || review.status === "self_assessment_in_progress";

  let actionLabel = t("appraisal.viewAppraisal", "View my appraisal");
  let actionTo = `/review?review=${review.id}`;
  if (!notOpen && review.status === "not_started") {
    actionLabel = t("appraisal.startSelf", "Start my self-assessment");
    actionTo = `/review?review=${review.id}&tab=self-assessment`;
  } else if (!notOpen && review.status === "self_assessment_in_progress") {
    actionLabel = t("appraisal.continueSelf", "Continue my self-assessment");
    actionTo = `/review?review=${review.id}&tab=self-assessment`;
  } else if (review.status === "employee_response_pending") {
    actionLabel = t("appraisal.readOutcome", "Read the outcome and respond");
  }

  return (
    <section className="card overflow-hidden">
      <div className="bg-pme-navy px-5 py-4 text-white">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-white/70">
          {t("appraisal.yourAppraisal", "Your appraisal")}
        </p>
        <h2 className="mt-0.5 text-lg font-semibold">{appraisalName(review.appraisal_year)}</h2>
        <p className="mt-0.5 text-xs text-white/75">
          {manager && t("appraisal.withManager", "With {{manager}}", { manager })}
          {manager && review.review_period_start && " · "}
          {review.review_period_start &&
            `${formatDate(review.review_period_start)} – ${formatDate(review.review_period_end)}`}
        </p>
      </div>

      <div className="space-y-4 p-5">
        {notOpen ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p className="text-sm font-semibold text-gray-900">{t("appraisal.notOpenTitle", "Not open yet")}</p>
            <p className="mt-1 text-sm text-gray-600">
              {review.opens_on
                ? t("appraisal.opensOn", "Your appraisal opens on {{date}}. You'll get an email invitation with a link when it starts.", { date: formatDate(review.opens_on, "long") })
                : t("appraisal.opensSoon", "You'll get an email invitation with a link when your appraisal starts.")}
            </p>
          </div>
        ) : (
          <div className={`rounded-xl p-4 ${yourMove ? "border border-pme-blue/30 bg-pme-blue/5" : "border border-gray-200 bg-gray-50"}`}>
            <p className="flex items-center gap-2 text-sm font-semibold text-pme-navy">
              {yourMove ? (
                <ArrowRightCircle size={16} strokeWidth={2} aria-hidden className="text-pme-blue" />
              ) : (
                <Hourglass size={16} strokeWidth={2} aria-hidden className="text-gray-500" />
              )}
              {now.title}
            </p>
            <p className="mt-1 text-sm text-gray-600">{now.body}</p>
            {review.meeting && review.status !== "completed" && (
              <div className="mt-3">
                <MyMeetingCard reviewId={review.id} meeting={review.meeting} managerName={manager} />
              </div>
            )}
            {selfStage && selfDue && (
              <p className={`mt-2 text-xs font-semibold ${days != null && days < 0 ? "text-red-600" : "text-pme-navy"}`}>
                {t("appraisal.selfDueBy", "Please finish it by {{date}}", { date: formatDate(selfDue, "long") })} · {dueText(selfDue)}
              </p>
            )}
            <Link
              to={actionTo}
              className={[
                "mt-3 inline-flex w-full items-center justify-center rounded-lg py-3 text-sm font-semibold sm:w-auto sm:px-6",
                yourMove ? "bg-pme-blue text-white shadow hover:bg-pme-blue/90" : "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50",
              ].join(" ")}
            >
              {actionLabel}
            </Link>
          </div>
        )}

        <div>
          <p className="eyebrow mb-3">{t("appraisal.stepsTitle", "The steps")}</p>
          <AppraisalJourney
            status={notOpen ? "" : review.status}
            managerName={manager}
            dates={{
              self: review.self_assessment_deadline,
              peers: review.colleague_feedback_deadline,
              manager: review.manager_review_deadline,
            }}
          />
        </div>
      </div>
    </section>
  );
}

function HowAppraisalsWork({ defaultOpen }: { defaultOpen: boolean }) {
  const { t } = useTranslation();
  return (
    <details className="card group p-4" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-semibold text-pme-navy">
        <span className="flex items-center gap-2">
          <Info size={16} strokeWidth={2} aria-hidden className="text-pme-blue" />
          {t("appraisal.howTitle", "How appraisals work at PME")}
        </span>
        <ChevronDown size={16} aria-hidden className="text-gray-400 transition group-open:rotate-180" />
      </summary>
      <div className="mt-4 space-y-4">
        <p className="text-sm text-gray-600">
          {t(
            "appraisal.howIntro",
            "Your appraisal is a chance to look back on your year with your manager and agree what's next. It's measured against your job description and PME's behaviours.",
          )}
        </p>
        <AppraisalJourney status="" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("appraisal.seeTitle", "What you see, and when")}
          </p>
          <ul className="mt-2 space-y-1.5 text-sm text-gray-600">
            <li className="flex gap-2"><Lock size={14} aria-hidden className="mt-0.5 shrink-0 text-gray-400" />{t("appraisal.see1", "Your manager's ratings and comments are shown to you once the appraisal is complete.")}</li>
            <li className="flex gap-2"><Users size={14} aria-hidden className="mt-0.5 shrink-0 text-gray-400" />{t("appraisal.see2", "Colleague feedback reaches you as themes. You are never told who said what.")}</li>
            <li className="flex gap-2"><Mail size={14} aria-hidden className="mt-0.5 shrink-0 text-gray-400" />{t("appraisal.see3", "Each step starts with an email, so you'll always know when it's your turn.")}</li>
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("appraisal.reflectTitle", "How you describe your own year")}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {t("appraisal.reflectIntro", "In your self-assessment you choose a word for each part of your job. It's how you see it, not a score.")}
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {reflectionScale("self").map((l) => (
              <li key={l.value}>
                <span className="font-medium text-gray-800">{l.label}</span> <span className="text-gray-500">— {l.hint}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {t("appraisal.scaleTitle", "The rating your manager gives")}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {t("appraisal.scaleIntro", "Your manager rates on the company scale. A 3 means a solid job, and most good performers sit there. A 4 or 5 always comes with specific examples.")}
          </p>
          <ul className="mt-2 space-y-1 text-sm">
            {ratingScale().slice().reverse().map((l) => (
              <li key={l.value} className="flex gap-2">
                <span className="w-5 shrink-0 font-bold text-pme-blue">{l.value}</span>
                <span><span className="font-medium text-gray-800">{l.label}</span> <span className="text-gray-500">— {l.hint}</span></span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

/* ── Team Reviews Tab ────────────────────────────────────────────────────── */

function TeamReviewsTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [colleagueModal, setColleagueModal] = useState<Review | null>(null);
  // Matrix is manager-only: this tab already loads role_filter=manager, so
  // every review here has the current user as its line manager.
  const [matrixReviewId, setMatrixReviewId] = useState<number | null>(null);

  const [past, setPast] = useState<Review[]>([]);

  const load = useCallback(async () => {
    try {
      const data = await api.get<Review[]>("/api/reviews?role_filter=manager");
      setReviews(data.filter((r) => r.status !== "completed" && r.status !== "cancelled"));
      setPast(data.filter((r) => r.status === "completed"));
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <p className="py-8 text-center text-sm text-gray-400">{t("common.loading")}</p>;
  if (reviews.length === 0 && past.length === 0) return <p className="py-8 text-center text-sm text-gray-500">{t("performance.noTeamReviews")}</p>;

  return (
    <>
      {past.length > 0 && reviews.length > 0 && (
        <h2 className="eyebrow">{t("appraisal.teamInProgress", "In progress")}</h2>
      )}
      {past.length > 0 && reviews.length === 0 && (
        <p className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500 shadow-sm">
          {t("appraisal.teamNoneOpen", "No appraisals open for your team right now.")}
        </p>
      )}
      {reviews.map((r) => {
        const canManageColleagues = r.status === "self_assessment_submitted" || r.status === "colleague_feedback_in_progress";
        const canStartManager = r.status === "manager_review_in_progress" || r.status === "colleague_feedback_complete";
        return (
          <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">{r.employee_name}</p>
                <p className="text-xs text-gray-500">{appraisalName(r.appraisal_year)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.manager_review_deadline && (
                  <span className="text-[10px] text-gray-400">{t("performance.due")}: {DATE_FMT.format(new Date(r.manager_review_deadline))}</span>
                )}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${reviewStatusBadge(r.status)}`}>
                  {reviewStatusLabel(r.status)}
                </span>
              </div>
            </div>
            <div className="mt-2">
              <ReviewStagePips status={r.status} />
            </div>
            {isMeetingStage(r.status) && (
              <div className="mt-3">
                <MeetingPanel reviewId={r.id} employeeName={r.employee_name ?? ""} status={r.status} meeting={r.meeting} compact onChanged={() => void load()} />
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {canManageColleagues && (
                <button
                  type="button"
                  onClick={() => setColleagueModal(r)}
                  className="rounded border border-blue-300 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50"
                >
                  {t("performance.selectColleagues")}
                </button>
              )}
              {(r.colleague_count ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setMatrixReviewId(r.id)}
                  className="rounded border border-purple-300 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50"
                >
                  {t("performance.colleagueFeedbackMatrix")}
                </button>
              )}
              {!canManageColleagues && (r.colleague_count ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setColleagueModal(r)}
                  className="rounded border border-purple-300 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50"
                >
                  {t("performance.viewPeerFeedback")}
                </button>
              )}
              {canManageColleagues && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await api.post(`/api/reviews/${r.id}/skip-colleague-feedback`);
                      void load();
                    } catch (err) {
                      if (err instanceof ApiError) alert(err.message);
                    }
                  }}
                  className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  {t("performance.skipColleagueFeedback")}
                </button>
              )}
              {canStartManager && (
                <button
                  type="button"
                  onClick={() => navigate(`/review?review=${r.id}&tab=manager-review&step=1`)}
                  className="rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
                >
                  {r.status === "manager_review_in_progress" ? t("performance.continueManagerReview") : t("performance.startManagerReview")}
                </button>
              )}
            </div>
          </div>
        );
      })}
      {past.length > 0 && <TeamAppraisalHistory reviews={past} />}
      {matrixReviewId !== null && (
        <ColleagueFeedbackMatrix
          reviewId={matrixReviewId}
          onClose={() => setMatrixReviewId(null)}
        />
      )}
      {colleagueModal && (
        <ColleagueModal
          review={colleagueModal}
          onClose={() => { setColleagueModal(null); void load(); }}
        />
      )}
    </>
  );
}

/* ── History of the appraisals this manager has done ─────────────────────── */

/** When the appraisal was held: the meeting, else the recorded date, else the period end. */
function appraisalHeldOn(r: Review): string | null {
  return r.meeting_date ?? r.review_date ?? r.review_period_end ?? null;
}

function TeamAppraisalHistory({ reviews }: { reviews: Review[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  // One group per person, newest appraisal first; people with the most
  // recent appraisal come first.
  const groups = (() => {
    const byPerson = new Map<number, Review[]>();
    for (const r of reviews) {
      const key = r.employee_id ?? -r.id;
      const list = byPerson.get(key) ?? [];
      list.push(r);
      byPerson.set(key, list);
    }
    const out = [...byPerson.values()].map((list) =>
      [...list].sort((a, b) => (appraisalHeldOn(b) ?? "").localeCompare(appraisalHeldOn(a) ?? "") || b.id - a.id),
    );
    out.sort((a, b) => (appraisalHeldOn(b[0]) ?? "").localeCompare(appraisalHeldOn(a[0]) ?? ""));
    return out;
  })();

  const q = query.trim().toLowerCase();
  const shown = q ? groups.filter((g) => (g[0].employee_name ?? "").toLowerCase().includes(q)) : groups;

  return (
    <section className="mt-6" aria-labelledby="team-history-title">
      <h2 id="team-history-title" className="eyebrow flex items-center gap-1.5">
        <History size={14} strokeWidth={1.75} aria-hidden />
        {t("appraisal.teamHistoryTitle", "Appraisals you have done")}
      </h2>
      <p className="mt-1 text-xs text-gray-500">
        {t(
          "appraisal.teamHistoryIntro",
          "Finished appraisals where you were the line manager. Open one to see what was agreed: the rating, objectives, development plan and the meeting summary.",
        )}
      </p>
      {groups.length > 5 && (
        <label className="mt-3 flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3">
          <Search size={14} className="text-gray-400" aria-hidden />
          <span className="sr-only">{t("appraisal.teamHistorySearch", "Find a person")}</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("appraisal.teamHistorySearch", "Find a person")}
            className="min-h-[40px] w-full bg-transparent text-sm outline-none"
          />
        </label>
      )}
      <div className="mt-3 space-y-3">
        {shown.length === 0 && (
          <p className="text-sm text-gray-500">{t("appraisal.teamHistoryNoMatch", "Nobody matches that name.")}</p>
        )}
        {shown.map((list) => {
          const first = list[0];
          return (
            <div key={first.employee_id ?? first.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-gray-900">{first.employee_name}</p>
                {first.employee_active === false && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                    {t("appraisal.teamHistoryLeft", "No longer at PME")}
                  </span>
                )}
                <span className="text-xs text-gray-400">
                  {list.length === 1
                    ? t("appraisal.teamHistoryOne", "1 appraisal")
                    : t("appraisal.teamHistoryMany", "{{count}} appraisals", { count: list.length })}
                </span>
              </div>
              <ul className="mt-2 divide-y divide-gray-100">
                {list.map((r) => {
                  const held = appraisalHeldOn(r);
                  const rating = r.overall_agreed_rating ?? r.overall_manager_rating ?? null;
                  return (
                    <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800">
                          {appraisalName(r.appraisal_year)}
                          {held && (
                            <span className="font-normal text-gray-500">
                              {" · "}
                              {r.is_historical
                                ? t("appraisal.teamHistoryRecorded", "recorded {{date}}", { date: formatDate(held) })
                                : r.meeting_date
                                  ? t("appraisal.teamHistoryMet", "meeting {{date}}", { date: formatDate(held) })
                                  : formatDate(held)}
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-600">
                          {r.overall_agreed_rating != null
                            ? t("appraisal.agreedRating", "Agreed rating")
                            : t("appraisal.teamHistoryYourRating", "Your rating")}
                          {": "}
                          <span className="font-semibold text-gray-800">{ratingText(rating)}</span>
                        </p>
                        {(r.objectives_total ?? 0) > 0 && (
                          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-600">
                            <Target size={12} aria-hidden className="text-pme-blue" />
                            {t("appraisal.teamHistoryObjectives", "Objectives: {{done}} of {{total}} done", { done: r.objectives_done ?? 0, total: r.objectives_total })}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {r.is_historical && (
                            <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                              {t("appraisal.teamHistoryOutside", "Recorded outside PME People")}
                            </span>
                          )}
                          {!r.is_historical && r.feedback_released === false && (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                              {t("appraisal.teamHistoryNotShared", "Outcome not shared with them yet")}
                            </span>
                          )}
                          {r.has_response && (
                            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-pme-blue">
                              {t("appraisal.teamHistoryReplied", "They added a response")}
                            </span>
                          )}
                          {r.check_in_plan && !r.check_in_plan.stopped && (
                            <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-700">
                              {t("appraisal.teamHistoryCheckIns", "Catch-ups {{cadence}}", { cadence: r.check_in_plan.cadence_label })}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/review?review=${r.id}`)}
                        className="inline-flex min-h-[40px] items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-pme-blue hover:bg-gray-50"
                      >
                        {t("appraisal.teamHistoryOpen", "Open")}
                        <ArrowRightCircle size={14} aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── Colleague Selection Modal ───────────────────────────────────────────── */

function ColleagueModal({ review, onClose }: { review: Review; onClose: () => void }) {
  const { t } = useTranslation();
  const [colleagues, setColleagues] = useState<ColleagueFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [focusAreas, setFocusAreas] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [ratingWords, setRatingWords] = useState(false);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [resendNote, setResendNote] = useState<{ id: number; ok: boolean; text: string } | null>(null);

  // The manager may add/remove peers only while feedback is being collected;
  // otherwise the modal is a read-only view of the feedback received.
  const canManage = review.status === "self_assessment_submitted" || review.status === "colleague_feedback_in_progress";
  const todayIso = new Date().toISOString().slice(0, 10);
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const loadColleagues = useCallback(async () => {
    try {
      // NB: this endpoint returns { colleagues, focus_areas } — not a bare array.
      const data = await api.get<{ colleagues: ColleagueFeedbackItem[]; focus_areas: string[] | null; rating_words?: boolean }>(`/api/reviews/${review.id}/colleagues`);
      setColleagues(data?.colleagues ?? []);
      setRatingWords(!!data?.rating_words);
    } catch { /* ignore */ }
    setLoading(false);
  }, [review.id]);

  useEffect(() => { void loadColleagues(); }, [loadColleagues]);

  const addColleague = async (id: number | null) => {
    if (!id) return;
    setAdding(true);
    try {
      await api.post(`/api/reviews/${review.id}/colleagues`, { colleague_id: id, focus_area: "general" });
      await loadColleagues();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setAdding(false);
  };

  const removeColleague = async (feedbackId: number) => {
    setRemovingId(feedbackId);
    try {
      await api.delete(`/api/reviews/colleagues/${feedbackId}`);
      setColleagues((prev) => prev.filter((c) => c.id !== feedbackId));
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
    setRemovingId(null);
  };

  const resend = async (feedbackId: number) => {
    setResendingId(feedbackId);
    try {
      const r = await api.post<{ sent: boolean; message: string }>(`/api/reviews/colleague-feedback/${feedbackId}/resend`);
      setResendNote({ id: feedbackId, ok: r.sent, text: r.message });
    } catch (err) {
      setResendNote({ id: feedbackId, ok: false, text: err instanceof ApiError ? err.message : t("team.resendFailed", "Couldn't resend. Try again.") });
    }
    setResendingId(null);
  };

  const submitted = colleagues.filter((c) => c.status === "submitted").length;
  const total = colleagues.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {canManage
            ? t("performance.selectColleaguesFor", { name: review.employee_name ?? "" })
            : t("performance.peerFeedbackFor", { name: review.employee_name ?? "" })}
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          {canManage ? t("performance.colleagueRecommendation") : t("performance.peerFeedbackViewHint")}
        </p>

        {loading ? (
          <p className="text-sm text-gray-400">{t("common.loading")}</p>
        ) : (
          <>
            {total > 0 && (
              <p className="text-xs font-medium text-gray-600 mb-2">
                {t("performance.colleagueProgress", { submitted, total })}
              </p>
            )}
            <div className="space-y-2 mb-4 max-h-72 overflow-y-auto">
              {colleagues.map((col) => {
                const isSubmitted = col.status === "submitted";
                const overdue = !isSubmitted && !!col.deadline && col.deadline <= todayIso;
                const isOpen = expanded.has(col.id);
                return (
                  <div key={col.id} className="rounded border border-gray-100 bg-gray-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium text-gray-800">{col.colleague_name}</span>
                        <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${isSubmitted ? "bg-green-50 text-green-700" : overdue ? "bg-gray-100 text-gray-500" : "bg-amber-50 text-amber-700"}`}>
                          {isSubmitted ? t("performance.submitted") : overdue ? t("performance.overdue") : t("performance.awaiting")}
                        </span>
                        {!isSubmitted && col.deadline && (
                          <span className="ml-1.5 text-[10px] text-gray-400">{t("performance.due")}: {col.deadline}</span>
                        )}
                        {canManage && (
                          <input
                            type="text"
                            placeholder={t("performance.focusAreaPlaceholder")}
                            value={focusAreas[col.id] ?? col.focus_area ?? ""}
                            onChange={(e) => setFocusAreas((prev) => ({ ...prev, [col.id]: e.target.value }))}
                            className="mt-1 block w-full rounded border border-gray-200 px-2 py-1 text-xs text-gray-700"
                          />
                        )}
                        {!canManage && col.focus_area && col.focus_area !== "general" && (
                          <span className="ml-2 text-[10px] text-gray-400">({col.focus_area})</span>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {isSubmitted && (col.responses?.length ?? 0) > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleExpanded(col.id)}
                            className="rounded border border-purple-200 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50"
                          >
                            {isOpen ? t("performance.hideFeedback") : t("performance.viewFeedback")}
                          </button>
                        )}
                        {!isSubmitted && col.invited_at && !["completed", "cancelled"].includes(review.status) && (
                          <button
                            type="button"
                            onClick={() => void resend(col.id)}
                            disabled={resendingId === col.id}
                            title={t("team.resendTitle", "Send {{name}} the feedback request again", { name: col.colleague_name })}
                            className="rounded border border-pme-blue/30 px-2 py-1 text-xs font-medium text-pme-blue hover:bg-pme-sky disabled:opacity-50"
                          >
                            {resendingId === col.id ? t("team.resending", "Sending…") : t("team.resend", "Resend")}
                          </button>
                        )}
                        {canManage && !isSubmitted && (
                          <button
                            type="button"
                            onClick={() => void removeColleague(col.id)}
                            disabled={removingId === col.id}
                            className="rounded border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-50"
                          >
                            {removingId === col.id ? "..." : t("common.delete")}
                          </button>
                        )}
                      </div>
                    </div>
                    {resendNote?.id === col.id && (
                      <p className={`mt-1 text-[11px] ${resendNote.ok ? "text-green-700" : "text-amber-700"}`} aria-live="polite">{resendNote.text}</p>
                    )}
                    {isSubmitted && isOpen && (
                      <div className="mt-2 space-y-2 border-t border-gray-200 pt-2">
                        {(col.responses ?? []).map((resp) => (
                          <div key={resp.id}>
                            <p className="text-xs font-medium text-gray-600">{resp.question}</p>
                            {resp.answer && <p className="text-xs text-gray-700 mt-0.5 whitespace-pre-wrap">{resp.answer}</p>}
                            {resp.rating != null && <p className="text-xs text-purple-600 mt-0.5">{ratingWords ? t("appraisal.theirView", "Their view") : t("performance.rating")}: {selfRatingText(resp.rating, ratingWords)}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {canManage && (
              <div className="mb-4">
                <EmployeeSearch
                  value={null}
                  onChange={(id) => void addColleague(id)}
                  placeholder={t("performance.addColleaguePlaceholder")}
                  className="input text-sm"
                  disabled={adding}
                  excludeIds={[review.employee_id ?? 0, ...colleagues.map((c) => c.colleague_id)]}
                />
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50">
            {canManage ? t("common.close") : t("common.done")}
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => { setSaving(true); onClose(); }}
              disabled={saving}
              className="rounded-lg bg-pme-blue px-3 py-2 text-xs font-semibold text-white hover:bg-pme-blue/90 disabled:opacity-50"
            >
              {t("common.save")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Tasks Tab ──────────────────────────────────────────────────────────── */

function priorityBadge(p: string): string {
  if (p === "urgent") return "bg-red-100 text-red-800";
  if (p === "high") return "bg-orange-100 text-orange-800";
  if (p === "medium") return "bg-blue-100 text-blue-700";
  return "bg-gray-100 text-gray-600";
}

function dueLabel(d: string | null, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (!d) return t("tasks.noDueDate");
  const diff = Math.round((new Date(d).getTime() - Date.now()) / 86400000);
  if (diff < 0) return t("tasks.overdueBy", { days: Math.abs(diff) });
  if (diff === 0) return t("tasks.dueToday");
  return t("tasks.dueIn", { days: diff });
}

function TasksTab() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<ActionTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"open" | "completed">("open");
  const [completing, setCompleting] = useState<number | null>(null);
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    try {
      const status = filter === "completed" ? "completed" : undefined;
      const url = status ? `/api/tasks/my?status=${status}` : "/api/tasks/my";
      setTasks(await api.get<ActionTaskRow[]>(url));
    } catch { /* ignore */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { setLoading(true); void load(); }, [load]);

  const doComplete = async (id: number) => {
    try {
      await api.post(`/api/tasks/${id}/complete`, notes ? { completion_notes: notes } : {});
      setCompleting(null);
      setNotes("");
      void load();
    } catch (err) {
      if (err instanceof ApiError) alert(err.message);
    }
  };

  return (
    <>
      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setFilter("open")}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === "open" ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600"}`}
        >
          {t("tasks.pending")}
        </button>
        <button
          type="button"
          onClick={() => setFilter("completed")}
          className={`rounded-full px-3 py-1 text-xs font-semibold ${filter === "completed" ? "bg-pme-blue text-white" : "bg-gray-100 text-gray-600"}`}
        >
          {t("tasks.completed")}
        </button>
      </div>

      {loading && <p className="py-8 text-center text-sm text-gray-400">{t("common.loading")}</p>}
      {!loading && tasks.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-500">{t("tasks.noTasks")}</p>
          <p className="text-xs text-gray-400 mt-1">{t("tasks.noTasksDescription")}</p>
        </div>
      )}
      {!loading && tasks.map((task) => (
        <div key={task.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900">{task.title}</p>
              {task.employee_name && (
                <p className="text-xs text-gray-500 mt-0.5">{t("tasks.regarding")}: {task.employee_name}</p>
              )}
              {task.description && (
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{task.description}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${priorityBadge(task.priority)}`}>
                {t(`tasks.priority${task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}`)}
              </span>
              <span className={`text-[10px] font-medium ${task.due_date && new Date(task.due_date) < new Date() && task.status !== "completed" ? "text-red-600" : "text-gray-400"}`}>
                {dueLabel(task.due_date, t)}
              </span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {task.source_type && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                {task.source_type === "review_360" ? t("tasks.sourceReview") : task.source_type === "manual" ? t("tasks.sourceManual") : task.source_type}
              </span>
            )}
            {task.action_url && (
              <button
                type="button"
                onClick={() => navigate(task.action_url!)}
                className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100"
              >
                {t("tasks.actionLink")} →
              </button>
            )}
            {task.status !== "completed" && (
              <button
                type="button"
                onClick={() => setCompleting(task.id)}
                className="ml-auto rounded border border-green-300 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50"
              >
                {t("tasks.markComplete")}
              </button>
            )}
            {task.status === "completed" && task.completed_at && (
              <span className="ml-auto text-[10px] text-green-600">
                <Check size={13} strokeWidth={2.5} aria-hidden className="mr-1 inline-block align-[-2px]" />{DATE_FMT.format(new Date(task.completed_at))}
              </span>
            )}
          </div>

          {completing === task.id && (
            <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
              <textarea
                rows={2}
                placeholder={t("tasks.completionNotesPlaceholder")}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-pme-blue focus:outline-none"
              />
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => { setCompleting(null); setNotes(""); }} className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100">
                  {t("common.cancel")}
                </button>
                <button type="button" onClick={() => void doComplete(task.id)} className="rounded bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700">
                  {t("tasks.confirmComplete")}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </>
  );
}

/* ── Tab button ──────────────────────────────────────────────────────────── */

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "whitespace-nowrap px-3 py-2 text-sm font-semibold",
        active ? "border-b-2 border-pme-blue text-pme-blue" : "text-gray-500",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
